/**
 * Sky and atmospheric haze.
 *
 * The fog is doing far more work than it looks like. It hides tile-mesh seams,
 * LOD pops and whatever the photogrammetry backdrop looks like up close, and
 * Hong Kong genuinely is hazy, so the cheat reads as accuracy. Sky colour and
 * fog colour must match exactly or a hard band appears at the horizon.
 */
import {
  BackSide,
  Color,
  Fog,
  Mesh,
  ShaderMaterial,
  Scene,
  SphereGeometry,
} from 'three';

/** Colour at the horizon. Also the fog colour - these must stay equal. */
export const HORIZON_COLOUR = 0xc8d2da;
const ZENITH_COLOUR = 0x5f8bbd;

/** Fog range. Beyond ~2 km nothing in the corridor is meant to be legible. */
export const FOG_NEAR = 165;
export const FOG_FAR = 1900;
export const FOG_DEFAULTS = { near: FOG_NEAR, far: FOG_FAR } as const;

const VERT = /* glsl */ `
  varying vec3 vWorldPosition;
  void main() {
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

/**
 * A raw ShaderMaterial does NOT get three's output pipeline for free. Without
 * the tone-mapping and colour-space chunks below, the shader writes linear
 * values straight out while the rest of the scene is tone mapped and encoded -
 * so the sky renders far too dark and stops matching the fog, which is the one
 * thing it must do. Including them puts it through exactly the same output
 * path as every other material.
 */
const FRAG = /* glsl */ `
  uniform vec3 uZenith;
  uniform vec3 uHorizon;
  varying vec3 vWorldPosition;

  #include <tonemapping_pars_fragment>

  void main() {
    float h = normalize(vWorldPosition).y;
    // Bias the gradient down so the haze band sits above the horizon line,
    // the way it does over the harbour on a clear-ish day.
    float t = pow(clamp(h, 0.0, 1.0), 0.42);
    vec3 c = mix(uHorizon, uZenith, t);
    // Below the horizon, stay flat horizon colour so the fog meets it seamlessly.
    c = mix(uHorizon, c, step(0.0, h));
    gl_FragColor = vec4(c, 1.0);

    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

function parseFogOverride(query: URLSearchParams): { near: number; far: number } {
  const near = Number(query.get('fogNear'));
  const far = Number(query.get('fogFar'));
  return {
    near: Number.isFinite(near) && near >= 50 && near <= 500 ? near : FOG_DEFAULTS.near,
    far: Number.isFinite(far) && far >= 500 && far <= 4000 ? far : FOG_DEFAULTS.far,
  };
}

export function setupSky(scene: Scene, query: URLSearchParams = new URLSearchParams(location.search)): void {
  const horizon = new Color(HORIZON_COLOUR).convertSRGBToLinear();
  const zenith = new Color(ZENITH_COLOUR).convertSRGBToLinear();

  const material = new ShaderMaterial({
    uniforms: {
      uZenith: { value: zenith },
      uHorizon: { value: horizon },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    side: BackSide,
    depthWrite: false,
    fog: false,
  });

  const sky = new Mesh(new SphereGeometry(2500, 24, 16), material);
  sky.name = 'sky';
  sky.renderOrder = 1;
  sky.frustumCulled = false;
  scene.add(sky);

  const fog = parseFogOverride(query);
  scene.fog = new Fog(new Color(HORIZON_COLOUR), fog.near, fog.far);
  scene.background = new Color(HORIZON_COLOUR);
}
